/**
 * PlanBuilderCard
 *
 * Hero card shown at the top of the homepage when the user
 * hasn't completed the plan builder flow yet.
 * When complete, shows a compact banner with step-icon grid.
 */

import React from "react";
import { View, Text, Pressable } from "react-native";
import {
  TestTube,
  Building2,
  Calendar,
  Heart,
  ArrowRight,
  CheckCircle,
  Pencil,
  FileText,
  BookOpen,
  Sparkles,
  Syringe,
  ClipboardCheck,
  Scale,
  Snowflake,
  Clock,
  Check,
  type LucideIcon,
} from "lucide-react-native";
import type { StepStatus } from "../../plan.types";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { GlassPillCTA } from "../../components/hero/GlassPillCTA";
import { TexturedHeroCard } from "../../components/hero/TexturedHeroCard";

type StepIconInfo = {
  id: string;
  title: string;
  icon?: string;
  status: StepStatus;
};

type Props = {
  onStart: () => void;
  hasProgress?: boolean;
  isComplete?: boolean;
  journeyTitle?: string;
  /** Steps with their completion status, shown as icon grid when complete */
  stepIcons?: StepIconInfo[];
  /** Called when a step icon is tapped — receives the step id */
  onStepPress?: (stepId: string) => void;
};

const ICON_MAP: Record<string, LucideIcon> = {
  FileText,
  BookOpen,
  TestTube,
  Sparkles,
  Building2,
  Syringe,
  ClipboardCheck,
  Scale,
  Snowflake,
  Clock,
  Calendar,
};

const BENEFITS = [
  { icon: TestTube, textKey: "plan_builder_card.benefit_testing" },
  { icon: Building2, textKey: "plan_builder_card.benefit_clinics" },
  { icon: Calendar, textKey: "plan_builder_card.benefit_timeline" },
  { icon: Heart, textKey: "plan_builder_card.benefit_guidance" },
];

function StepIconSquare({
  step,
  onPress,
}: {
  step: StepIconInfo;
  onPress?: () => void;
}) {
  const capturePress = usePressCapture("StepIconSquare");
  const Icon: LucideIcon = step.icon
    ? (ICON_MAP[step.icon] ?? BookOpen)
    : BookOpen;
  const isComplete = step.status === "complete";
  const isActive = step.status === "in-progress";

  return (
    <Pressable
      onPress={
        onPress
          ? () => {
              capturePress(pressLabel(step.title), step.id);
              onPress();
            }
          : undefined
      }
      style={{
        flex: 1,
        height: 36,
        borderRadius: 8,
        backgroundColor: isComplete
          ? "rgba(245, 200, 130, 0.22)"
          : isActive
            ? "rgba(255, 255, 255, 0.15)"
            : "rgba(255, 255, 255, 0.06)",
        alignItems: "center",
        justifyContent: "center",
        borderWidth: isActive ? 1 : 0,
        borderColor: isActive ? "rgba(255, 255, 255, 0.25)" : "transparent",
      }}
    >
      {isComplete ? (
        <Check size={14} color="#F5C882" strokeWidth={2.5} />
      ) : (
        <Icon
          size={15}
          color={
            isActive ? "rgba(255, 255, 255, 0.85)" : "rgba(255, 255, 255, 0.35)"
          }
          strokeWidth={1.5}
        />
      )}
    </Pressable>
  );
}

export default function PlanBuilderCard({
  onStart,
  hasProgress,
  isComplete,
  journeyTitle,
  stepIcons,
  onStepPress,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PlanBuilderCard");
  if (isComplete) {
    return (
      <TexturedHeroCard
        borderRadius={16}
        padding={18}
        style={{ marginTop: 16 }}
      >
        {/* Title row — tapping Edit opens the plan builder */}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
          }}
        >
          <CheckCircle size={20} color="rgba(255, 255, 255, 0.7)" />
          <View style={{ flex: 1 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                fontWeight: "600",
                color: "#FFFFFF",
                lineHeight: 20,
              }}
            >
              {journeyTitle ?? t("plan_builder_card.your_plan_is_ready")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "400",
                color: "rgba(255, 255, 255, 0.55)",
                lineHeight: 18,
                marginTop: 2,
              }}
            >
              {t("plan_builder_card.personalized_based_on")}
            </Text>
          </View>
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("plan_builder_card.edit")),
                "plan_builder_card.edit",
              );
              onStart();
            }}
            hitSlop={8}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Pencil size={14} color="rgba(255, 255, 255, 0.6)" />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "500",
                color: "rgba(255, 255, 255, 0.6)",
              }}
            >
              {t("plan_builder_card.edit")}
            </Text>
          </Pressable>
        </View>

        {/* Step icon grid — each icon scrolls to its step */}
        {stepIcons && stepIcons.length > 0 && (
          <View
            style={{
              flexDirection: "row",
              gap: 6,
              marginTop: 14,
            }}
          >
            {stepIcons.map((step) => (
              <StepIconSquare
                key={step.id}
                step={step}
                onPress={() => onStepPress?.(step.id)}
              />
            ))}
          </View>
        )}
      </TexturedHeroCard>
    );
  }

  return (
    <TexturedHeroCard
      onPress={onStart}
      pressLabelText={
        hasProgress
          ? t("plan_builder_card.continue")
          : t("plan_builder_card.get_started")
      }
      action={
        hasProgress
          ? "plan_builder_card.continue"
          : "plan_builder_card.get_started"
      }
      style={{ marginTop: 16 }}
    >
      {/* Main text */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          fontWeight: "700",
          color: "#FFFFFF",
          lineHeight: 26,
          marginBottom: 6,
        }}
      >
        {hasProgress
          ? t("plan_builder_card.continue_building")
          : t("plan_builder_card.lets_build")}
      </Text>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          fontWeight: "400",
          color: "rgba(255, 255, 255, 0.65)",
          lineHeight: 20,
          marginBottom: 20,
        }}
      >
        {hasProgress
          ? t("plan_builder_card.pick_up_where")
          : t("plan_builder_card.tailor_everything")}
      </Text>

      {/* Benefits */}
      <View style={{ gap: 14, marginBottom: 24 }}>
        {BENEFITS.map((b) => (
          <View
            key={b.textKey}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}
          >
            <b.icon size={16} color="rgba(255, 255, 255, 0.5)" />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "500",
                color: "rgba(255, 255, 255, 0.85)",
                lineHeight: 18,
                flex: 1,
              }}
            >
              {t(b.textKey)}
            </Text>
          </View>
        ))}
      </View>

      <GlassPillCTA
        label={
          hasProgress
            ? t("plan_builder_card.continue")
            : t("plan_builder_card.get_started")
        }
        icon={ArrowRight}
      />

      {/* Time estimate */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          fontWeight: "400",
          color: "rgba(255, 255, 255, 0.4)",
          textAlign: "center",
          marginTop: 10,
        }}
      >
        {t("plan_builder_card.just_3_minutes")}
      </Text>
    </TexturedHeroCard>
  );
}
